import { API_BASE_URL, CHATBOT_URL } from 'config';
import { useTheme } from 'contexts/ThemeContext';
import { USER_TOKEN, USER_VIEW_MODE, TEMP_USER_TOKEN } from 'helpers/storage';
import { useEffect } from 'react';

const ChatbotModal = () => {
  const userToken = USER_TOKEN.get()?.replace('Bearer ', '').trim();
  const tempUserToken = TEMP_USER_TOKEN.get()?.replace('Bearer ', '').trim();
  const token = tempUserToken || userToken;
  const role = USER_VIEW_MODE.get();
  const backendUrl = API_BASE_URL;

  const { isDarkMode } = useTheme();
  const rawTheme = isDarkMode ? 'dark' : 'light';
  const theme =
    String(rawTheme).toLowerCase().trim() === 'dark' ? 'dark' : 'light';
  // Script and iframe must use different ids — widget looks up iframe by widgetId.
  const widgetId = 'chatbot-widget';
  const scriptId = 'chatbot-widget-script';

  useEffect(() => {
    if (!token || !CHATBOT_URL) return;

    // 1️⃣ Set global config FIRST (before script runs)
    window.ChatbotConfig = {
      chatbotUrl: CHATBOT_URL,
      token,
      origin: backendUrl,
      role,
      theme,
      widgetId,
    };

    // 2️⃣ Prevent duplicate script load
    if (document.getElementById(scriptId)) return;

    // 3️⃣ Inject script
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = `${CHATBOT_URL}/chatbot-widget.js`;
    script.async = true;

    document.body.appendChild(script);

    // 4️⃣ Cleanup on unmount / re-run
    return () => {
      script.remove();
      document.getElementById(widgetId)?.remove();
      delete window.ChatbotConfig;
    };
  }, [token, backendUrl, role]);

  useEffect(() => {
    if (!CHATBOT_URL) return;

    window.ChatbotConfig = {
      ...window.ChatbotConfig,
      theme,
    };

    const sendTheme = () => {
      const iframe = document.getElementById(widgetId);
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            type: 'theme',
            theme,
          },
          CHATBOT_URL,
        );
      }
    };

    // Try immediately (in case ready)
    sendTheme();

    // Also try after short delay in case iframe just mounted
    const t1 = setTimeout(sendTheme, 100);
    const t2 = setTimeout(sendTheme, 400);

    // Attach load listener if iframe exists
    const iframeEl = document.getElementById(widgetId);
    if (iframeEl) {
      iframeEl.addEventListener('load', sendTheme, { once: true });
    }

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      if (iframeEl) {
        iframeEl.removeEventListener('load', sendTheme);
      }
    };
  }, [theme]);

  return null; // widget handles its own UI
};

export default ChatbotModal;
